export type KeyState = 'pressed' | 'held' | 'released' | 'up';

export interface MouseState {
  x: number;
  y: number;
  deltaX: number;
  deltaY: number;
  leftButton: boolean;
  rightButton: boolean;
  middleButton: boolean;
  wheelDelta: number;
}

export interface InputBindings {
  moveForward: string[];
  moveBackward: string[];
  moveLeft: string[];
  moveRight: string[];
  rotateLeft: string[];
  rotateRight: string[];
  rotateUp: string[];
  rotateDown: string[];
  jump: string[];
  run: string[];
  crouch: string[];
  toggleCamera: string[];
  inventory: string[];
  pause: string[];
  interact: string[];
  slot1: string[];
  slot2: string[];
  slot3: string[];
  slot4: string[];
  slot5: string[];
  slot6: string[];
  slot7: string[];
  slot8: string[];
  slot9: string[];
}

const DEFAULT_BINDINGS: InputBindings = {
  moveForward: ['KeyW'],
  moveBackward: ['KeyS'],
  moveLeft: ['KeyA'],
  moveRight: ['KeyD'],
  rotateLeft: ['ArrowLeft'],
  rotateRight: ['ArrowRight'],
  rotateUp: ['ArrowUp'],
  rotateDown: ['ArrowDown'],
  jump: ['Space'],
  run: ['ShiftLeft', 'ShiftRight'],
  crouch: ['ControlLeft', 'ControlRight'],
  toggleCamera: ['KeyV'],
  inventory: ['KeyE'],
  pause: ['Escape'],
  interact: ['KeyF'],
  slot1: ['Digit1'],
  slot2: ['Digit2'],
  slot3: ['Digit3'],
  slot4: ['Digit4'],
  slot5: ['Digit5'],
  slot6: ['Digit6'],
  slot7: ['Digit7'],
  slot8: ['Digit8'],
  slot9: ['Digit9'],
};

export class InputManager {
  private static instance: InputManager | null = null;

  private keys: Map<string, KeyState> = new Map();
  private mouse: MouseState = {
    x: 0,
    y: 0,
    deltaX: 0,
    deltaY: 0,
    leftButton: false,
    rightButton: false,
    middleButton: false,
    wheelDelta: 0,
  };
  private previousMouse: MouseState = { ...this.mouse };

  private bindings: InputBindings = { ...DEFAULT_BINDINGS };
  private canvas: HTMLCanvasElement | null = null;

  private constructor() {
    this.setupEventListeners();
  }

  public static getInstance(): InputManager {
    if (!InputManager.instance) {
      InputManager.instance = new InputManager();
    }
    return InputManager.instance;
  }

  public attachToCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;

    // Use pointer events with pointer capture for reliable right-click drag
    canvas.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    canvas.addEventListener('pointerup', (e) => this.onPointerUp(e));
    canvas.addEventListener('pointermove', (e) => this.onPointerMove(e));
    canvas.addEventListener('pointercancel', (e) => this.onPointerUp(e));

    // Prevent context menu on canvas
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private setupEventListeners(): void {
    // Keyboard events
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('keyup', (e) => this.onKeyUp(e));

    // Wheel event
    window.addEventListener('wheel', (e) => this.onWheel(e));

    // Prevent context menu globally
    document.addEventListener('contextmenu', (e) => e.preventDefault(), true);
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.repeat) return;

    const code = event.code;

    // Prevent browser defaults for function keys used in game
    if (code === 'F3' || code === 'F5' || code === 'F11') {
      event.preventDefault();
    }

    if (!this.keys.has(code) || this.keys.get(code) === 'up') {
      this.keys.set(code, 'pressed');
    }
  }

  private onKeyUp(event: KeyboardEvent): void {
    this.keys.set(event.code, 'released');
  }

  private onPointerMove(event: PointerEvent): void {
    // Calculate delta when right button is held
    if (this.mouse.rightButton) {
      const dx = event.clientX - this.mouse.x;
      const dy = event.clientY - this.mouse.y;
      this.mouse.deltaX += dx;
      this.mouse.deltaY += dy;
    }
    this.mouse.x = event.clientX;
    this.mouse.y = event.clientY;
  }

  private onPointerDown(event: PointerEvent): void {
    switch (event.button) {
      case 0:
        this.mouse.leftButton = true;
        break;
      case 1:
        this.mouse.middleButton = true;
        break;
      case 2:
        this.mouse.rightButton = true;
        // Capture pointer to receive events even outside element
        if (this.canvas) {
          this.canvas.setPointerCapture(event.pointerId);
        }
        break;
    }
  }

  private onPointerUp(event: PointerEvent): void {
    switch (event.button) {
      case 0:
        this.mouse.leftButton = false;
        break;
      case 1:
        this.mouse.middleButton = false;
        break;
      case 2:
        this.mouse.rightButton = false;
        // Release pointer capture
        if (this.canvas && this.canvas.hasPointerCapture(event.pointerId)) {
          this.canvas.releasePointerCapture(event.pointerId);
        }
        break;
    }
  }

  private onWheel(event: WheelEvent): void {
    this.mouse.wheelDelta = Math.sign(event.deltaY);
  }

  public update(): void {
    // Update key states
    for (const [key, state] of this.keys) {
      if (state === 'pressed') {
        this.keys.set(key, 'held');
      } else if (state === 'released') {
        this.keys.set(key, 'up');
      }
    }

    // Update mouse state
    this.previousMouse = { ...this.mouse };
    this.mouse.deltaX = 0;
    this.mouse.deltaY = 0;
    this.mouse.wheelDelta = 0;
  }

  // Key state queries
  public isKeyPressed(code: string): boolean {
    return this.keys.get(code) === 'pressed';
  }

  public isKeyHeld(code: string): boolean {
    const state = this.keys.get(code);
    return state === 'pressed' || state === 'held';
  }

  public isKeyReleased(code: string): boolean {
    return this.keys.get(code) === 'released';
  }

  // Action queries (using bindings)
  public isActionPressed(action: keyof InputBindings): boolean {
    const keys = this.bindings[action];
    return keys.some((key) => this.isKeyPressed(key));
  }

  public isActionHeld(action: keyof InputBindings): boolean {
    const keys = this.bindings[action];
    return keys.some((key) => this.isKeyHeld(key));
  }

  public isActionReleased(action: keyof InputBindings): boolean {
    const keys = this.bindings[action];
    return keys.some((key) => this.isKeyReleased(key));
  }

  // Mouse queries
  public getMousePosition(): { x: number; y: number } {
    return { x: this.mouse.x, y: this.mouse.y };
  }

  public getMouseDelta(): { x: number; y: number } {
    return { x: this.mouse.deltaX, y: this.mouse.deltaY };
  }

  public isMouseButtonDown(button: 'left' | 'right' | 'middle'): boolean {
    switch (button) {
      case 'left':
        return this.mouse.leftButton;
      case 'right':
        return this.mouse.rightButton;
      case 'middle':
        return this.mouse.middleButton;
    }
  }

  public isMouseButtonPressed(button: 'left' | 'right' | 'middle'): boolean {
    const current = this.isMouseButtonDown(button);
    const previous = button === 'left'
      ? this.previousMouse.leftButton
      : button === 'right'
        ? this.previousMouse.rightButton
        : this.previousMouse.middleButton;
    return current && !previous;
  }

  public getWheelDelta(): number {
    return this.mouse.wheelDelta;
  }

  public isPointerLockedState(): boolean {
    // Camera rotation is now controlled by holding right mouse button
    // instead of pointer lock
    return this.mouse.rightButton;
  }

  // Movement helpers
  public getMovementVector(): { x: number; z: number } {
    let x = 0;
    let z = 0;

    if (this.isActionHeld('moveForward')) z += 1;
    if (this.isActionHeld('moveBackward')) z -= 1;
    if (this.isActionHeld('moveLeft')) x -= 1;
    if (this.isActionHeld('moveRight')) x += 1;

    // Normalize diagonal movement
    const length = Math.sqrt(x * x + z * z);
    if (length > 0) {
      x /= length;
      z /= length;
    }

    return { x, z };
  }

  // Keyboard rotation (arrow keys)
  public getRotationInput(): { x: number; y: number } {
    let x = 0; // Horizontal (yaw)
    let y = 0; // Vertical (pitch)

    if (this.isActionHeld('rotateLeft')) x -= 1;
    if (this.isActionHeld('rotateRight')) x += 1;
    if (this.isActionHeld('rotateUp')) y -= 1;
    if (this.isActionHeld('rotateDown')) y += 1;

    return { x, y };
  }

  // Binding management
  public setBinding(action: keyof InputBindings, keys: string[]): void {
    this.bindings[action] = keys;
  }

  public getBinding(action: keyof InputBindings): string[] {
    return this.bindings[action];
  }

  public resetBindings(): void {
    this.bindings = { ...DEFAULT_BINDINGS };
  }
}
